import {
  QuoteResponse,
  QuoteLineTypeValues,
  QuoteItemLine,
  QuantityBreak,
  HydratedQuantityBreak,
  HydratedQuoteLine,
  HydratedQuoteItemLine,
  HydratedQuoteDiscountLine,
} from "@forgepoint/shared";
import type { QuoteEditorData } from "./QuoteEditor";

/** Converts an editor `yyyy-MM-dd` date (empty when unset) to the API value. */
export const getDateOnlyParam = (date: string) => (date ? date : null);

/**
 * Merges a saved quote from the server into the local editor state.
 * Server-computed fields (prices, totals, conversions, effective items) come
 * from the server response; local editable fields win.
 */
export const mergeQuoteState = (
  serverQuote: QuoteResponse,
  localData: QuoteEditorData,
): { quote: QuoteResponse; hydratedLines: HydratedQuoteLine[] } => {
  const hydratedLines: HydratedQuoteLine[] = localData.lines.map((line) => {
    if (line.type === QuoteLineTypeValues.ITEM) {
      return mergeItemLine(line, serverQuote);
    }
    if (line.type === QuoteLineTypeValues.DISCOUNT) {
      const serverLine = serverQuote.lines?.find(
        (l): l is HydratedQuoteDiscountLine =>
          l.type === QuoteLineTypeValues.DISCOUNT && l.id === line.id,
      );
      return {
        ...line,
        amount: serverLine?.amount ?? null,
        convertedAmount: serverLine?.convertedAmount ?? null,
      };
    }
    return line;
  });

  const mergedQuote: QuoteResponse = {
    ...serverQuote,
    name: localData.name || serverQuote.name,
    customerCompanyId:
      localData.customerCompanyId || serverQuote.customerCompanyId,
    priceListId: localData.priceListId ?? serverQuote.priceListId,
    shippingAddressId:
      localData.shippingAddressId ?? serverQuote.shippingAddressId,
    deliveryTerms: localData.deliveryTerms || serverQuote.deliveryTerms,
    paymentTerms: localData.paymentTerms || serverQuote.paymentTerms,
    termsOfService: localData.termsOfService || serverQuote.termsOfService,
    quoteFooter: localData.quoteFooter || serverQuote.quoteFooter,
    expirationDate: getDateOnlyParam(localData.expirationDate),
    quoteDate: getDateOnlyParam(localData.quoteDate),
    lines: hydratedLines,
  };

  return { quote: mergedQuote, hydratedLines };
};

const mergeItemLine = (
  line: QuoteItemLine,
  serverQuote: QuoteResponse,
): HydratedQuoteItemLine => {
  // Match by line id for correct handling of duplicate items
  const serverLine = serverQuote.lines?.find(
    (l): l is HydratedQuoteItemLine =>
      l.type === QuoteLineTypeValues.ITEM && l.id === line.id,
  );
  return {
    ...serverLine,
    id: line.id,
    type: line.type,
    itemId: line.itemId,
    description: line.description,
    quantityBreaks: line.quantityBreaks.map((qb, idx) =>
      mergeQuantityBreak(qb, serverLine?.quantityBreaks?.[idx]),
    ),
    confidenceScore: serverLine?.confidenceScore ?? line.confidenceScore,
    reasoning: serverLine?.reasoning ?? line.reasoning,
    item: serverLine?.item ?? null,
    amount: serverLine?.amount ?? null,
    customFields: line.customFields,
    itemOverrides: line.itemOverrides,
    unitOverride: line.unitOverride,
    dimensionUnitOverride: line.dimensionUnitOverride,
  };
};

const mergeQuantityBreak = (
  qb: QuantityBreak,
  serverBreak: HydratedQuantityBreak | undefined,
): HydratedQuantityBreak => {
  const local = {
    quantity: qb.quantity,
    unitPriceOverride: qb.unitPriceOverride ?? null,
  };
  if (serverBreak) {
    return { ...serverBreak, ...local };
  }
  return {
    ...local,
    unitPriceSource: null,
    priceListLineId: null,
    unitPrice: null,
    amount: null,
    convertedUnitPrice: null,
    convertedAmount: null,
    totalWeight: null,
    totalLength: null,
    totalWidth: null,
    totalHeight: null,
  };
};
